import { trackConfig, type IFramework } from '@/framework';
import { Layer } from '@/layers/layer';
import { checkIsDarkColor } from '@/utils/color-kit';
import { TextMeasurer } from '@/utils/text-kit';
import { LayerType, type IBackgroundLayerProps, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { getDefaultLayerWithEffectsForShortcut, getDefaultShapeTextLayerForShortcut } from '../config';
import { MIN_FONT_SIZE } from '../constants';
import { createTextLayer, getFontSize, type PureTextKind } from '../utils';

export interface IShortcut {
    /**
     * 创建纯文本图层
     * @param kind - 文本类型
     * @returns
     */
    createPureTextLayer(kind: PureTextKind, stageIndex?: number): Promise<ILayerProps>;
    /**
     * 文本图层快捷方式：添加默认标题
     * @returns
     */
    addByDefaultTitle(byMousePosition?: boolean): Promise<ILayerProps>;

    /**
     * 文本图层快捷方式：添加默认子标题
     * @returns
     */
    addByDefaultSubTitle(byMousePosition?: boolean): Promise<ILayerProps>;

    /**
     * 文本图层快捷方式：添加默认正文
     * @returns
     */
    addByDefaultContent(byMousePosition?: boolean): Promise<ILayerProps>;

    /**
     * 文本图层快捷方式：添加默认变形文字
     * @param materialID - 素材 ID，由于各环境的素材 id 存在差异，需要传由外部传入
     * @returns
     */
    addByDefaultShapeText(materialID: string): ILayerProps;

    /**
     * 文本图层快捷方式：添加默认特效文字
     * @returns
     */
    addByDefaultEffectText(byMousePosition?: boolean): ILayerProps;

    /**
     * 文本图层快捷方式：通过自定义文本图层数据创建文本图层
     * @param layerData - 部分文本图层数据
     * @param options - 配置项
     * @returns
     */
    addByCustomLayerData(
        layerData: Partial<ITextLayerProps>,
        options?: { byStraight?: boolean; byMousePosition?: boolean },
    ): Promise<ILayerProps>;
}

const DEFAULT_LIGHT_COLOR = '#ffffffff';
const DEFAULT_DARK_COLOR = '#000000ff';

export class Shortcut implements IShortcut {
    constructor(
        private framework: IFramework,
        private baseLayer: Layer,
    ) {}

    async createPureTextLayer(kind: PureTextKind, stageIndex?: number) {
        const stageIdx = stageIndex !== undefined ? stageIndex : this.framework.stage.getActiveStageIndex();
        const stageSize = this.framework.getSizeByIndex(stageIdx);

        const fontSize = getFontSize(kind, stageSize);
        const finalFontSize = Math.max(MIN_FONT_SIZE, fontSize);

        const layerData: Partial<ITextLayerProps> = {
            fontSize: finalFontSize,
        };

        // 根据背景色确认默认文字颜色
        const backgroundColor = this.getCurrentBackgroundColor();
        if (backgroundColor) {
            const isDark = checkIsDarkColor(backgroundColor);
            layerData.color = isDark ? DEFAULT_LIGHT_COLOR : DEFAULT_DARK_COLOR;
        }

        const layer = await createTextLayer(stageSize, layerData, {
            fontSubsetLoader: this.framework.createTextSubset.bind(this.framework),
        });

        return layer;
    }

    private getCurrentBackgroundColor() {
        const stageLayers = this.framework.stage.getActiveStageLayers();
        if (stageLayers.length === 0) return null;

        const backgroundLayer = stageLayers.find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);
        if (!backgroundLayer) return null;

        const layerData = backgroundLayer.data as IBackgroundLayerProps;
        return layerData.backgroundColor;
    }

    async addByDefaultTitle(byMousePosition?: boolean) {
        let layer = await this.createPureTextLayer('title');

        if (byMousePosition) {
            layer = this.baseLayer.addByMousePosition({ layer, trackFunc: trackConfig[LayerType.TEXT_LAYER].add });
        } else {
            layer = this.baseLayer.addByStraight({
                layer,
                trackFunc: trackConfig[LayerType.TEXT_LAYER].add,
            });
        }

        return layer;
    }

    async addByDefaultSubTitle(byMousePosition?: boolean) {
        let layer = await this.createPureTextLayer('subTitle');

        if (byMousePosition) {
            layer = this.baseLayer.addByMousePosition({ layer, trackFunc: trackConfig[LayerType.TEXT_LAYER].add });
        } else {
            layer = this.baseLayer.addByStraight({
                layer,
                trackFunc: trackConfig[LayerType.TEXT_LAYER].add,
            });
        }

        return layer;
    }

    async addByDefaultContent(byMousePosition?: boolean) {
        let layer = await this.createPureTextLayer('content');

        if (byMousePosition) {
            layer = this.baseLayer.addByMousePosition({ layer, trackFunc: trackConfig[LayerType.TEXT_LAYER].add });
        } else {
            layer = this.baseLayer.addByStraight({
                layer,
                trackFunc: trackConfig[LayerType.TEXT_LAYER].add,
            });
        }

        return layer;
    }

    addByDefaultShapeText(materialID: string) {
        const stageIdx = this.framework.stage.getActiveStageIndex();
        const stageSize = this.framework.getSizeByIndex(stageIdx);

        let isDarkBG = false;
        // 根据背景色确认默认文字颜色
        const backgroundColor = this.getCurrentBackgroundColor();
        if (backgroundColor) {
            isDarkBG = checkIsDarkColor(backgroundColor);
        }

        let layer = getDefaultShapeTextLayerForShortcut(stageSize, materialID, isDarkBG);

        layer = this.baseLayer.addByStraight({
            layer,
            trackFunc: trackConfig[LayerType.TEXT_LAYER].add,
        });

        return layer;
    }

    addByDefaultEffectText(byMousePosition?: boolean) {
        const stageIdx = this.framework.stage.getActiveStageIndex();
        const stageSize = this.framework.getSizeByIndex(stageIdx);

        let layer = getDefaultLayerWithEffectsForShortcut(stageSize);

        if (byMousePosition) {
            layer = this.baseLayer.addByMousePosition({ layer, trackFunc: trackConfig[LayerType.TEXT_LAYER].add });
        } else {
            layer = this.baseLayer.addByStraight({
                layer,
                trackFunc: trackConfig[LayerType.TEXT_LAYER].add,
            });
        }

        return layer;
    }

    async addByCustomLayerData(
        layerData: Partial<ITextLayerProps>,
        options = { byStraight: false, byMousePosition: false },
    ) {
        const { byMousePosition } = options;

        const stageIdx = this.framework.stage.getActiveStageIndex();
        const stageSize = this.framework.getSizeByIndex(stageIdx);

        let layer = await createTextLayer(stageSize, layerData);
        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: true,
            keepConstantHeight: false,
        });

        layer.width = textSize.width;
        layer.height = textSize.height;

        if (byMousePosition) {
            layer = this.baseLayer.addByMousePosition({ layer, trackFunc: trackConfig[LayerType.TEXT_LAYER].add });
        } else {
            layer = this.baseLayer.addByStraight({
                layer,
                trackFunc: trackConfig[LayerType.TEXT_LAYER].add,
            });
        }
        return layer;
    }
}
